import { useQueryClient } from '@tanstack/react-query'
import { ImagePlus, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import type { RecordPhoto } from '../useRecordDraft'
import { Spinner } from '@/components/ui/misc'
import { api, ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/avif']
const MAX_BYTES = 8 * 1024 * 1024

/**
 * Uploads go straight from the browser to Neon Object Storage using a short-lived
 * presigned URL, so the API never handles the image bytes. The record row is only
 * updated once the PUT succeeds.
 */
export function PhotoUploader({
  recordId,
  photos,
  disabled,
  onChange,
}: {
  recordId: string
  photos: RecordPhoto[]
  disabled?: boolean
  onChange: (photos: RecordPhoto[]) => void
}) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)

  const upload = async (files: FileList | File[]) => {
    const chosen = Array.from(files)
    if (chosen.length === 0) return

    setBusy(true)
    try {
      for (const file of chosen) {
        if (!ACCEPTED.includes(file.type)) {
          toast.error(`${file.name} is not a JPEG, PNG, WebP or AVIF image.`)
          continue
        }
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name} is larger than 8 MB.`)
          continue
        }

        const { key, uploadUrl } = await api.post<{ key: string; uploadUrl: string }>(
          `/records/${recordId}/photos/presign`,
          { filename: file.name, contentType: file.type, sizeBytes: file.size },
        )

        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'content-type': file.type },
        })
        if (!response.ok) throw new Error(`Upload failed with ${response.status}`)

        const result = await api.post<{ record: { photos: RecordPhoto[] } }>(
          `/records/${recordId}/photos`,
          { key, filename: file.name, contentType: file.type, sizeBytes: file.size },
        )
        onChange(result.record.photos)
      }
      await queryClient.invalidateQueries({ queryKey: ['record', recordId] })
    } catch (failure) {
      toast.error(
        failure instanceof ApiError ? failure.message : 'That photo could not be uploaded.',
      )
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const remove = async (photoId: string) => {
    try {
      await api.delete(`/records/${recordId}/photos/${photoId}`)
      onChange(photos.filter((photo) => photo.id !== photoId))
    } catch {
      toast.error('That photo could not be removed.')
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {photos.map((photo) => (
          <figure
            key={photo.id}
            className="group relative size-28 overflow-hidden rounded-[var(--radius-control)] border border-line"
          >
            <img src={photo.url} alt={photo.filename} className="size-full object-cover" />
            {!disabled && (
              <button
                type="button"
                onClick={() => void remove(photo.id)}
                className="absolute right-1 top-1 rounded-md bg-ink/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                aria-label={`Remove ${photo.filename}`}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </figure>
        ))}

        {!disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              void upload(event.dataTransfer.files)
            }}
            className={cn(
              'flex size-28 flex-col items-center justify-center gap-1 rounded-[var(--radius-control)]',
              'border border-dashed border-line text-[11px] text-ink-muted transition-colors',
              'hover:border-brand-line hover:bg-brand-soft hover:text-brand',
              dragging && 'border-brand bg-brand-soft text-brand',
            )}
          >
            {busy ? <Spinner /> : <ImagePlus className="size-5" />}
            {busy ? 'Uploading' : 'Add photo'}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(event) => event.target.files && void upload(event.target.files)}
      />

      <p className="text-xs text-ink-faint">
        JPEG, PNG, WebP or AVIF, up to 8 MB each. Photograph the swatch under even light.
      </p>
    </div>
  )
}
